import React, { useEffect, useMemo, useState } from 'react'
import { Minus, Plus, X } from 'lucide-react'
import { getFeasibilityForm } from '../../helpers/feasibility'
import FeasibilityCalculationResult from '../../components/FeasibilityCalculationResult'

const FeasibilityPreviewModal = ({ isOpen, property, renovatedValue, preview, onCalculate, onClose }) => {
  const initialForm = useMemo(() => getFeasibilityForm(property, renovatedValue), [property, renovatedValue])
  const [form, setForm] = useState(initialForm)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isOpen) return
    setForm(initialForm)
    setError('')
  }, [isOpen, initialForm])

  useEffect(() => {
    if (!isOpen) return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !submitting) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, submitting, onClose])

  if (!isOpen) return null

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }))
  const updateCost = (index, field, value) => setForm((current) => ({
    ...current,
    additionalCosts: current.additionalCosts.map((cost, costIndex) => costIndex === index ? { ...cost, [field]: value } : cost),
  }))
  const removeCost = (index) => setForm((current) => ({ ...current, additionalCosts: current.additionalCosts.filter((_, costIndex) => costIndex !== index) }))

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      await onCalculate(form)
    } catch (requestError) {
      setError(requestError.message || 'Kalkulaci se nepodařilo vytvořit.')
    } finally {
      setSubmitting(false)
    }
  }

  const valuationSource = Number.isFinite(Number(renovatedValue?.anchorPpm2))
    ? `${Math.round(renovatedValue.anchorPpm2).toLocaleString('cs-CZ')} Kč/m² · odhad po rekonstrukci`
    : 'Doplňte vlastní cílovou cenu za m²'

  return (
    <div className="fixed inset-0 z-[2100] flex items-center justify-center bg-slate-950/45 p-3 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="feasibility-title">
      <button type="button" className="absolute inset-0 cursor-default" aria-label="Zavřít" onClick={() => !submitting && onClose()} />
      <div className="relative z-10 flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 sm:px-6">
          <div>
            <h2 id="feasibility-title" className="text-xl font-semibold text-slate-900">Záměr — rekonstrukce a prodej</h2>
            <p className="mt-1 text-sm text-slate-500">Uložené předpoklady pro {property?.title || 'vybranou nemovitost'}.</p>
          </div>
          <button type="button" onClick={onClose} disabled={submitting} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 disabled:text-slate-300" aria-label="Zavřít">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-5 sm:px-6">
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
            <form className="space-y-6" onSubmit={handleSubmit}>
              <section className="rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-900">Nákup a prodej</h3>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <label className="text-sm text-slate-700">Prodejní obytná plocha (m²)
                    <input type="number" min="1" step="0.1" value={form.saleableAreaM2} onChange={(event) => updateField('saleableAreaM2', event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
                  </label>
                  <label className="text-sm text-slate-700">Kupní cena
                    <input type="number" min="1" step="1" value={form.purchasePrice} onChange={(event) => updateField('purchasePrice', event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
                  </label>
                  <label className="text-sm text-slate-700 sm:col-span-2">Cílová prodejní cena / m²
                    <input type="number" min="1" step="1" value={form.targetPricePerM2} onChange={(event) => updateField('targetPricePerM2', event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
                    <span className="mt-1 block text-xs text-slate-500">{valuationSource}</span>
                  </label>
                </div>
              </section>

              <section className="rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-900">Rekonstrukce a dodatečné náklady</h3>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <label className="text-sm text-slate-700">Výměra rekonstrukce (m²)
                    <input type="number" min="1" step="0.1" value={form.renovationAreaM2} onChange={(event) => updateField('renovationAreaM2', event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
                  </label>
                  <label className="text-sm text-slate-700">Náklad rekonstrukce / m²
                    <input type="number" min="0" step="1" value={form.renovationCostPerM2} onChange={(event) => updateField('renovationCostPerM2', event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
                  </label>
                  <label className="text-sm text-slate-700">Rezerva (%)
                    <input type="number" min="0" max="100" step="0.1" value={form.contingencyPercent} onChange={(event) => updateField('contingencyPercent', event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
                  </label>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="text-sm font-medium text-slate-700">Dodatečné náklady</div>
                  {form.additionalCosts.map((cost, index) => (
                    <div key={index} className="flex gap-2">
                      <input value={cost.name} onChange={(event) => updateCost(index, 'name', event.target.value)} placeholder="Název" className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm" />
                      <input type="number" min="0" step="1" value={cost.amount} onChange={(event) => updateCost(index, 'amount', event.target.value)} placeholder="CZK" className="w-32 rounded-md border border-slate-300 px-3 py-2 text-sm" />
                      <button type="button" onClick={() => removeCost(index)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100" aria-label="Odstranit náklad"><Minus className="h-4 w-4" /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => setForm((current) => ({ ...current, additionalCosts: [...current.additionalCosts, { name: '', amount: '' }] }))} className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"><Plus className="h-4 w-4" /> Přidat náklad</button>
                </div>
              </section>

              <section className="rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-900">Daň a můj podíl</h3>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <label className="text-sm text-slate-700">Daňový režim
                    <select value={form.taxRegime} onChange={(event) => updateField('taxRegime', event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2">
                      <option value="individual-nonbusiness">Fyzická osoba</option>
                      <option value="self-employed">OSVČ</option>
                      <option value="company-dividend">s.r.o. + výplata podílu</option>
                    </select>
                  </label>
                  <label className="text-sm text-slate-700">Můj podíl na zisku (%)
                    <input type="number" min="0" max="100" step="0.1" value={form.profitSharePercent} onChange={(event) => updateField('profitSharePercent', event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
                  </label>
                </div>
              </section>

              <section className="rounded-xl border border-slate-200 p-4">
                <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-slate-900">
                  <input type="checkbox" checked={form.financingEnabled} onChange={(event) => updateField('financingEnabled', event.target.checked)} className="h-4 w-4" />
                  Zahrnout financování
                </label>
                {form.financingEnabled && (
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <label className="text-sm text-slate-700">Typ úvěru
                      <select value={form.loanType} onChange={(event) => updateField('loanType', event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"><option value="annuity">Anuitní</option><option value="interest-only">Interest-only</option></select>
                    </label>
                    <label className="text-sm text-slate-700">Jistina
                      <input type="number" min="1" step="1" value={form.loanPrincipal} onChange={(event) => updateField('loanPrincipal', event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
                    </label>
                    <label className="text-sm text-slate-700">Sazba p.a. (%)
                      <input type="number" min="0" step="0.01" value={form.loanAnnualRatePercent} onChange={(event) => updateField('loanAnnualRatePercent', event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
                    </label>
                    <label className="text-sm text-slate-700">Délka úvěru (měsíce)
                      <input type="number" min="1" step="1" value={form.loanTermMonths} onChange={(event) => updateField('loanTermMonths', event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
                    </label>
                    <label className="text-sm text-slate-700">Měsíc čerpání
                      <input type="number" min="0" step="1" value={form.loanDrawMonth} onChange={(event) => updateField('loanDrawMonth', event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
                    </label>
                    <label className="text-sm text-slate-700">Vstupní poplatek
                      <input type="number" min="0" step="1" value={form.loanEntryFee} onChange={(event) => updateField('loanEntryFee', event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
                    </label>
                    <label className="text-sm text-slate-700 sm:col-span-2">Doba držení (měsíce)
                      <input type="number" min="1" step="1" value={form.holdingPeriodMonths} onChange={(event) => updateField('holdingPeriodMonths', event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
                    </label>
                  </div>
                )}
              </section>

              {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
              <div className="flex justify-end gap-3 pb-1">
                <button type="button" onClick={onClose} disabled={submitting} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">Zavřít</button>
                <button type="submit" disabled={submitting} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-blue-300">{submitting ? 'Počítám…' : 'Přepočítat flip'}</button>
              </div>
            </form>
            <div className="xl:sticky xl:top-0 xl:self-start">
              {preview ? <FeasibilityCalculationResult preview={preview} profitSharePercent={form.profitSharePercent} /> : (
                <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500">Doplňte předpoklady a spusťte kalkulaci. Předpoklady se uloží k této nemovitosti.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FeasibilityPreviewModal
